<template>
    <teleport to="body">
        <div
            ref="playMenuRef"
            v-show="showPlayMenu"
            class="bg-white shadow-lg rounded text-sm z-[100000]"
        >
            <div
                v-if="!disableSave"
                class="py-2 px-3 my-1 hover:bg-gray-100 cursor-pointer"
                @click="handleAction({ action: 'template' })"
            >
                Load Template...
            </div>
        </div>
    </teleport>
    <teleport to="body">
        <div class="fixed top-[6rem] left-[8px]">
            <button
                ref="playButtonRef"
                @click="togglePlayMenu(!showPlayMenu)"
                class="
                    w-10
                    h-10
                    flex
                    items-center
                    justify-center
                    rounded-full
                    shadow-xl
                    p-2
                    border border-gray-300
                    hover:scale-105 hover:bg-gray-200
                    transition
                "
            >
                <img src="/default-images/icons/play.png" alt="Play menu" />
            </button>
            <button
                v-if="!disableSave"
                class="
                    w-10
                    h-10
                    flex
                    items-center
                    justify-center
                    rounded-full
                    shadow-xl
                    p-2
                    border border-gray-300
                    hover:scale-105 hover:bg-gray-200
                    transition
                    mt-4
                "
                type="button"
                @click="handleAction({ action: 'background' })"
            >
                <img
                    src="/default-images/icons/background.png"
                    alt="Play menu"
                />
            </button>
        </div>
    </teleport>
    <ModalCustom
        :is-open="backgroundState.isBackgroundModalOpen"
        @close="backgroundState.isBackgroundModalOpen = false"
        title="Load Background"
        key="background"
    >
        <div
            class="flex flex-wrap gap-8 flex-start max-h-[50vh] overflow-y-auto"
        >
            <div
                class="
                    text-center
                    p-2
                    max-w-[300px]
                    flex flex-col
                    gap-2
                    hover:bg-gray-100
                    rounded-lg
                    items-center
                    justify-center
                    border-primary
                    text-primary
                    border-dashed border
                    cursor-pointer
                "
                key="none"
                @click="loadBackground({ name: 'None', image: 'none' })"
            >
                <div class="px-16">CLEAR BACKGROUND</div>
            </div>
            <div
                class="contents"
                v-for="(backgrounds, key) in backgroundState.backgrounds"
                :key="key"
            >
                <hr class="w-full" v-if="key != 0" />
                <div
                    class="
                        text-center
                        p-2
                        max-w-[300px]
                        flex flex-col
                        gap-2
                        hover:bg-gray-100
                        rounded-lg
                        border-gray-300 border
                        cursor-pointer
                    "
                    v-for="background in backgrounds"
                    :key="background.name"
                    @click="loadBackground(background)"
                >
                    <img :src="background.image" :alt="background.name" />
                    <div>
                        {{ background.name }}
                    </div>
                </div>
            </div>
        </div>
    </ModalCustom>
    <ModalCustom
        :is-open="templateState.isTemplateModalOpen"
        @close="templateState.isTemplateModalOpen = false"
    >
        <header class="flex items-center mb-4">
            <button
                v-if="templateState.activeTemplateGroup"
                @click="templateState.activeTemplateGroup = null"
                class="
                    w-10
                    h-10
                    flex
                    items-center
                    justify-center
                    bg-white
                    rounded-full
                    text-black
                    flex-grow-0 flex-shrink-0
                "
            >
                <ion-icon name="arrow-back-outline"></ion-icon>
            </button>
            <h3 class="mr-12 text-[16px]">{{ loadTemplateTitle }}</h3>
            <button
                type="button"
                class="rounded-md ml-auto leading-[0] p-1 hover:bg-gray-100"
                @click="
                    templateState.isTemplateModalOpen = false;
                    templateState.activeTemplateGroup = null;
                "
            >
                <ion-icon name="close"></ion-icon>
            </button>
        </header>
        <ul class="block pl-0" v-if="!templateState.activeTemplateGroup">
            <li
                class="block my-[1rem]"
                v-for="templateGroup in templateState.templateGroups"
                :key="templateGroup.id"
            >
                <button
                    class="
                        block
                        w-full
                        py-2
                        px-4
                        text-left
                        bg-gray-100
                        rounded-md
                        hover:bg-primary hover:bg-opacity-10
                        text-[16px]
                    "
                    @click="templateState.activeTemplateGroup = templateGroup"
                    type="button"
                >
                    {{ templateGroup.playbook_title }}
                </button>
            </li>
        </ul>
        <ul class="block pl-0" v-if="templateState.activeTemplateGroup">
            <li
                class="block my-[1rem]"
                v-for="template in templateState.activeTemplateGroup.plays"
                :key="template.play_id"
            >
                <button
                    class="
                        block
                        w-full
                        py-2
                        px-4
                        text-left
                        bg-gray-100
                        rounded-md
                        hover:bg-primary hover:bg-opacity-10
                        text-[16px]
                    "
                    @click="loadTemplate(template)"
                    type="button"
                >
                    {{ template.play_name }}
                </button>
            </li>
        </ul>
    </ModalCustom>
</template>

<script setup>
import { computed, onMounted, reactive, ref, toRaw } from "vue";
import AppInput from "./AppInput";
import ModalCustom from "./ModalCustom";
import WhiteboardMenuItem from "./WhiteboardMenuItem";
import AppButton from "./AppButton";
import { createPopper } from "@popperjs/core";
import LoadPlayModal from "./LoadPlayModal.vue";
import LoadVideoModal from "./LoadVideoModal.vue";
import { truncateJson } from "../utils/play";
import { sleep } from "../utils/timer";
import emitter from "tiny-emitter/instance";
import {
    basketballBg,
    footballBg,
    soccerBg,
    hockeyBg,
    fieldHockeyBg,
    lacrosseBg,
    volleyballBg,
} from "./whiteboard/backgrounds";

const props = defineProps([
    "roomId",
    "disableSave",
    "playSaveApi",
    "playListApi",
    "playbookApi",
    "videoApi",
    "videoGroupApi",
    "whiteboardApi",
    "code",
    "previewUrl",
    "setBackgroundApi",
    "background",
    "isPlay",
    "isTemplate",
    "play",
    "playSetApi",
    "playClearApi",
    "notes",
    "sport",
]);

const backgroundState = reactive({
    isBackgroundModalOpen: false,
    currentBackground: props.background,
    backgrounds: [],
});

const templateState = reactive({
    activeTemplateGroup: null,
    isTemplateModalOpen: false,
    templateGroups: [],
});

const loadTemplateTitle = computed(() => {
    if (templateState.activeTemplateGroup) {
        return templateState.activeTemplateGroup.playbook_title;
    }
    return "Load Template";
});

const handleAction = async (action) => {
    if (action.action === "template") {
        templateState.isTemplateModalOpen = true;
    }
    if (action.action === "background") {
        backgroundState.isBackgroundModalOpen = true;
    }
    showPlayMenu.value = false;
};

async function triggerPlayLoaded(play) {
    loadState.activePlay = play;
    await axios.post(props.playSetApi, {
        roomId: props.roomId,
        playId: play.play_id,
    });
    emitter.emit("wb-load-play", play);
}

emitter.on("clear-wb", function () {
    loadState.activePlay = null;
});

async function refreshData() {
    try {
        const templateApiUrl = new URL(props.playbookApi);
        templateApiUrl.searchParams.set("isTemplate", 1);
        templateState.templateGroups = (
            await axios.get(templateApiUrl.href)
        ).data;
    } catch {}
}

async function loadTemplate(template) {
    const dataTemplate = await axios.get("/get-play-data", {
        params: {
            play_id: template.play_id,
            code: props.code,
        },
    });
    const frame = document.getElementById("meeting_iframe");
    if (frame) {
        const rawData = toRaw(JSON.parse(dataTemplate.data.play_json));
        const truncated = truncateJson(rawData);

        frame.contentWindow.postMessage(truncated, props.whiteboardApi);
    }
    if (
        dataTemplate.data.background &&
        dataTemplate.data.background !== "none"
    ) {
        loadBackground({ image: dataTemplate.data.background });
    }
    templateState.isTemplateModalOpen = false;
    templateState.activeTemplateGroup = null;
    emitter.emit("wb-load-template", dataTemplate.data);
}

async function loadBackground(background) {
    const frame = document.getElementById("meeting_iframe");
    if (frame) {
        frame.contentWindow.postMessage(
            {
                action: "background",
                background: window.origin + background.image,
            },
            props.whiteboardApi
        );
    }
    emitter.emit("wb-change-background", background.image);

    backgroundState.isBackgroundModalOpen = false;
    backgroundState.currentBackground = background.image;
    if (!props.isPlay) {
        await axios.post(props.setBackgroundApi, {
            roomId: props.roomId,
            background: background.image,
        });
    }
}

const showPlayMenu = ref(false);
const playMenuRef = ref(null);
const playButtonRef = ref(null);
const togglePlayMenu = (value) => {
    showPlayMenu.value = value;
    if (value) {
        createPopper(playButtonRef.value, playMenuRef.value, {
            placement: "right",
            modifiers: [
                {
                    name: "offset",
                    options: {
                        offset: [0, 8],
                    },
                },
            ],
        }).forceUpdate();
    }
};

const isMobile = () => {
    return (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        ) ||
        screen.availWidth < 768 ||
        screen.availHeight < 480
    );
};

function loadSportBackground() {
    if (props.sport === "basketball") {
        backgroundState.backgrounds = [
            basketballBg,
            footballBg,
            soccerBg,
            hockeyBg,
            fieldHockeyBg,
            lacrosseBg,
            volleyballBg,
        ];
    } else {
        backgroundState.backgrounds = [
            footballBg,
            basketballBg,
            soccerBg,
            hockeyBg,
            fieldHockeyBg,
            lacrosseBg,
            volleyballBg,
        ];
    }
}

onMounted(() => {
    refreshData();
    loadSportBackground();

    const frame = document.getElementById("meeting_iframe");
    frame?.addEventListener("load", () => {
        const pingId = crypto.randomUUID();
        const contentWindow = frame.contentWindow;
        const interval = setInterval(() => {
            contentWindow.postMessage(
                { action: "ping", id: pingId },
                props.whiteboardApi
            );
        }, 1500);
        const listener = (event) => {
            if (event.data.action === "pong" && event.data.id === pingId) {
                clearInterval(interval);
                window.removeEventListener("message", listener);
                frame.contentWindow.postMessage(
                    {
                        action: "background",
                        background:
                            window.origin + (props.background ?? "none"),
                    },
                    props.whiteboardApi
                );
            }
        };
        window.addEventListener("message", listener);
    });

    window.addEventListener("message", function (event) {
        // Unlink play with the whiteboard
        if (
            event.data.action === "clear" &&
            !props.isPlay &&
            loadState.activePlay
        ) {
            axios.post(props.playClearApi, {
                roomId: props.roomId,
            });
            loadState.activePlay = null;

            if (!props.isPlay) {
                frame?.contentWindow.postMessage(
                    {
                        action: "setVideosCount",
                        count: 0,
                    },
                    props.whiteboardApi
                );
            }
        }

        if (event.data.action === "load_background") {
            loadBackground(event.data);
        }
    });
});
</script>

<style scoped></style>
